/**
 * Hook para obtener símbolos PTEL basados en tipología y zoom
 * 
 * SYM-07b - Conecta el sistema de símbolos con el visor Leaflet
 * 
 * @module usePTELSymbol
 * @version 1.0.0
 * @date 2024-12-31
 * 
 * @example
 * ```tsx
 * const { symbolPath, tipologia, confidence } = usePTELSymbol(infrastructure, zoom);
 * // → { symbolPath: "/assets/symbols/PNG/12mm/BOMBEROS.png", ... }
 * ```
 */

import { useMemo } from 'react';
import type { NormalizedInfrastructure, InfrastructureType } from '../types/infrastructure';
import {
  detectAndGetSymbol,
  getSymbolPath,
  type SizeCode,
  type CategoriaSimbolo,
  SimboloIcono,
  SIZE_TO_PIXELS,
} from '../lib/symbols/tipologiaSymbolMatcher';

// =============================================================================
// TIPOS
// =============================================================================

/** Resultado del hook usePTELSymbol */
export interface PTELSymbolResult {
  /** Ruta completa al archivo PNG */
  symbolPath: string;
  /** Tipología PTEL detectada */
  tipologia: CategoriaSimbolo | null;
  /** Símbolo asignado */
  simbolo: SimboloIcono;
  /** Tamaño seleccionado según zoom */
  size: SizeCode;
  /** Dimensión en píxeles */
  sizePixels: number;
  /** Confianza de detección (0 = fallback) */
  confidence: number;
  /** Keyword que produjo el match */
  matchedKeyword: string;
  /** Si usó fallback genérico */
  isFallback: boolean;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * Mapeo InfrastructureType → SimboloIcono para fallback
 * Cuando detectAndGetSymbol no encuentra match, usamos el tipo genérico
 * Las keys son los valores del enum InfrastructureType (strings)
 */
const INFRASTRUCTURE_TYPE_TO_SYMBOL: Partial<Record<string, SimboloIcono>> = {
  // Valores del enum InfrastructureType
  SANITARIO: SimboloIcono.CENTROS_SANITARIOS,
  EDUCATIVO: SimboloIcono.CENTROS_EDUCATIVOS,
  BOMBEROS: SimboloIcono.BOMBEROS,
  POLICIAL: SimboloIcono.POLICIA_LOCAL,
  EMERGENCIAS: SimboloIcono.CECOPAL,
  MUNICIPAL: SimboloIcono.EDIFICIOS_PUBLICOS,
  CULTURAL: SimboloIcono.CENTRO_CULTURAL,
  RELIGIOSO: SimboloIcono.EDIFICIO_RELIGIOSO,
  DEPORTIVO: SimboloIcono.PARQUE_OCIO,
  SOCIAL: SimboloIcono.SERVICIOS_SOCIALES,
  HIDRAULICO: SimboloIcono.DEPOSITOS,
  ENERGIA: SimboloIcono.CENTRAL_ELECTRICA,
  TELECOM: SimboloIcono.ANTENAS,
  COMBUSTIBLE: SimboloIcono.GASOLINERA,
  VIAL: SimboloIcono.APOYO_LOGISTICO,
  INDUSTRIAL: SimboloIcono.INDUSTRIA,
  GENERICO: SimboloIcono.EDIFICIOS_PUBLICOS,
};

/**
 * Símbolo por defecto cuando todo falla
 */
const DEFAULT_SYMBOL: SimboloIcono = SimboloIcono.EDIFICIOS_PUBLICOS;

// =============================================================================
// FUNCIONES
// =============================================================================

/**
 * Determina el tamaño de símbolo según nivel de zoom
 * 
 * @param zoom - Nivel de zoom del mapa Leaflet (0-20)
 * @returns Código de tamaño en mm
 * 
 * Escala:
 * - zoom ≤ 10: 06mm (23px) - Vista regional
 * - zoom ≤ 13: 08mm (30px) - Vista municipal
 * - zoom ≤ 16: 12mm (45px) - Vista urbana (DEFAULT)
 * - zoom ≤ 18: 20mm (76px) - Vista detalle
 * - zoom > 18: 28mm (106px) - Vista máximo detalle
 */
export function getSymbolSizeForZoom(zoom: number): SizeCode {
  if (zoom <= 10) return '06mm';
  if (zoom <= 13) return '08mm';
  if (zoom <= 16) return '12mm';
  if (zoom <= 18) return '20mm';
  return '28mm';
}

/**
 * Obtiene símbolo de fallback basado en InfrastructureType
 * Acepta tanto el enum como su valor string
 */
function getFallbackSymbol(type: InfrastructureType | string): SimboloIcono {
  // El type puede ser el valor del enum (ej: "SANITARIO") o el enum key
  const typeStr = typeof type === 'string' ? type : String(type);
  return INFRASTRUCTURE_TYPE_TO_SYMBOL[typeStr] ?? DEFAULT_SYMBOL;
}

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

/**
 * Hook para obtener el símbolo PTEL correcto según infraestructura y zoom
 * 
 * Flujo de detección:
 * 1. Intenta detectar tipología específica desde nombre + tipo
 * 2. Si falla, usa fallback basado en InfrastructureType
 * 3. Ajusta tamaño según nivel de zoom
 * 
 * @param infrastructure - Infraestructura normalizada
 * @param zoom - Nivel de zoom actual del mapa (default: 15)
 * @returns Resultado con ruta del símbolo y metadatos
 * 
 * @example
 * ```tsx
 * function MyMarker({ infrastructure }: Props) {
 *   const { symbolPath, sizePixels } = usePTELSymbol(infrastructure, 15);
 *   return <img src={symbolPath} width={sizePixels} />;
 * }
 * ```
 */
export function usePTELSymbol(
  infrastructure: NormalizedInfrastructure,
  zoom: number = 15
): PTELSymbolResult {
  return useMemo(() => {
    const { name, type, address } = infrastructure;
    // Construir texto de búsqueda: nombre + tipo + dirección
    const searchText = [name, type, address].filter(Boolean).join(' ');
    
    // Determinar tamaño según zoom
    const size = getSymbolSizeForZoom(zoom);
    const sizePixels = SIZE_TO_PIXELS[size];
    
    // Intentar detección automática
    const detection = detectAndGetSymbol(searchText, size, 'PNG');
    
    if (detection) {
      // Detección exitosa
      return {
        symbolPath: detection.path,
        tipologia: detection.tipologia,
        simbolo: detection.simbolo,
        size,
        sizePixels,
        confidence: detection.confidence,
        matchedKeyword: detection.matchedKeyword,
        isFallback: false,
      };
    }
    
    // Fallback basado en InfrastructureType
    const fallbackSymbol = getFallbackSymbol(type);
    const fallbackPath = getSymbolPath(fallbackSymbol, size, 'PNG');
    
    return {
      symbolPath: fallbackPath,
      tipologia: null,
      simbolo: fallbackSymbol,
      size,
      sizePixels,
      confidence: 0,
      matchedKeyword: '',
      isFallback: true,
    };
  // J1: deps en objeto completo — react-compiler infiere `infrastructure` como dep mínima necesaria
  }, [infrastructure, zoom]);
}

// =============================================================================
// FUNCIONES AUXILIARES EXPORTADAS
// =============================================================================

/**
 * Versión no-hook para usar fuera de componentes React
 * Útil para crear iconos Leaflet en funciones de utilidad
 */
export function getPTELSymbolPath(
  name: string,
  type: InfrastructureType,
  zoom: number = 15,
  description?: string
): string {
  const searchText = [name, type, description].filter(Boolean).join(' ');
  const size = getSymbolSizeForZoom(zoom);
  
  const detection = detectAndGetSymbol(searchText, size, 'PNG');
  
  if (detection) {
    return detection.path;
  }
  
  const fallbackSymbol = getFallbackSymbol(type);
  return getSymbolPath(fallbackSymbol, size, 'PNG');
}

/**
 * Precarga símbolos para mejorar rendimiento
 * Útil llamar al inicio de la app o al cargar datos
 */
export function preloadPTELSymbols(sizes: SizeCode[] = ['12mm']): Promise<void[]> {
  const allSymbols = Object.values(INFRASTRUCTURE_TYPE_TO_SYMBOL).filter((s): s is SimboloIcono => s !== undefined);
  const uniqueSymbols = [...new Set(allSymbols)];
  
  const preloadPromises: Promise<void>[] = [];
  
  for (const size of sizes) {
    for (const simbolo of uniqueSymbols) {
      const path = getSymbolPath(simbolo, size, 'PNG');
      preloadPromises.push(
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // No fallar si imagen no existe
          img.src = path;
        })
      );
    }
  }
  
  return Promise.all(preloadPromises);
}

// =============================================================================
// EXPORTS
// =============================================================================

// Re-export tipos necesarios desde tipologiaSymbolMatcher
export type { SizeCode, CategoriaSimbolo } from '../lib/symbols/tipologiaSymbolMatcher';
export { SimboloIcono } from '../lib/symbols/tipologiaSymbolMatcher';

export default usePTELSymbol;
